/**
 * hash router の純粋ロジック（docs/design/layout-and-navigation.md §1）。
 * DOM・window に触れない。`location.hash` の文字列を受け取り、Route を返す。
 */

export type LabKind = 'stack' | 'queue' | 'dict';
export type LabStep = 1 | 2 | 3;
export type MeterDepth = 1 | 2 | 3 | 'nlogn';

export type Route =
  /** ホーム = 問題一覧（docs/decisions/problem-first.md） */
  | { name: 'home' }
  /**
   * `#/session`（`#/drill` は別名）。index は 1 始まり。省略時は最初の未回答カード。
   * ナビからの導線は出さない（URL 直打ちだけで開く）
   */
  | { name: 'session'; index?: number }
  | { name: 'session-done' }
  | { name: 'card'; cardId: string }
  /** `#/lessons`: レッスン一覧（参照用。読了状態は出さない） */
  | { name: 'lessons' }
  /**
   * `#/lesson/<id>`（lesson-format.md §2.1 の id）。`#/lesson/<id>#<slug>` で節を指す（section。
   * problem-format.md §6.2。decodeURIComponent → NFC 済み）
   */
  | { name: 'lesson'; lessonId: string; section?: string }
  /** `#/problem/<id>`（problem-format.md §2.1 の id。例 abc123-b） */
  | { name: 'problem'; problemId: string }
  | { name: 'lab'; kind?: LabKind; step: LabStep }
  | { name: 'meter'; cardId?: string; n?: number; depth?: MeterDepth }
  | { name: 'settings'; section?: 'data' }
  | { name: 'keys' }
  | { name: 'not-found'; path: string };

export type RouteName = Route['name'];

export const LAB_KINDS: readonly LabKind[] = ['stack', 'queue', 'dict'];

const ID_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
/** schemas/problem.schema.json の $defs/problemId と同じ形。 */
export const PROBLEM_ID_PATTERN = /^abc[0-9]{3,4}-[a-g]$/;

function isLabKind(s: string): s is LabKind {
  return (LAB_KINDS as readonly string[]).includes(s);
}

function parseStep(raw: string | null): LabStep {
  if (raw === '2') return 2;
  if (raw === '3') return 3;
  return 1;
}

function parseDepth(raw: string | null): MeterDepth | undefined {
  switch (raw) {
    case '1':
      return 1;
    case '2':
      return 2;
    case '3':
      return 3;
    case 'nlogn':
      return 'nlogn';
    default:
      return undefined;
  }
}

function parsePositiveInt(raw: string | null | undefined): number | undefined {
  if (raw == null || !/^[1-9][0-9]{0,8}$/.test(raw)) return undefined;
  return Number(raw);
}

function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

/**
 * `#/lab/stack?step=2` のような hash を Route に変換する。先頭の `#` は有っても無くてもよい。
 * 先頭の `#` を除いた後にもう 1 つ `#` があれば、最初の `#` で path と section に分ける
 * （`#/lesson/<id>#<slug>`。lesson 以外に section が付いたら not-found。空の section は無いものとする）
 */
export function parseHash(hash: string): Route {
  let raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const sharp = raw.indexOf('#');
  let section: string | undefined;
  if (sharp !== -1) {
    const rawSection = raw.slice(sharp + 1);
    raw = raw.slice(0, sharp);
    if (rawSection !== '') {
      const decodedSection = safeDecode(rawSection);
      if (decodedSection === null) return { name: 'not-found', path: raw };
      const normalized = decodedSection.normalize('NFC');
      if (normalized !== '') section = normalized;
    }
  }
  const route = parsePath(raw);
  if (section === undefined) return route;
  if (route.name !== 'lesson') return { name: 'not-found', path: raw };
  return { ...route, section };
}

function parsePath(path: string): Route {
  let raw = path;
  if (raw === '' || raw === '/') return { name: 'home' };
  if (!raw.startsWith('/')) raw = `/${raw}`;

  const q = raw.indexOf('?');
  const pathPart = q === -1 ? raw : raw.slice(0, q);
  const query = new URLSearchParams(q === -1 ? '' : raw.slice(q + 1));
  const decoded = pathPart
    .split('/')
    .filter((s) => s !== '')
    .map(safeDecode);
  if (decoded.some((s) => s === null)) return { name: 'not-found', path: pathPart };
  const segs = decoded.filter((s): s is string => s !== null);
  const [head, second, ...rest] = segs;
  const notFound: Route = { name: 'not-found', path: pathPart };

  switch (head) {
    case undefined:
      return { name: 'home' };
    case 'session':
    case 'drill': {
      if (second === undefined) return { name: 'session' };
      if (rest.length > 0) return notFound;
      if (second === 'done') return { name: 'session-done' };
      const index = parsePositiveInt(second);
      return index === undefined ? notFound : { name: 'session', index };
    }
    case 'card':
      if (second === undefined || rest.length > 0 || !ID_PATTERN.test(second)) return notFound;
      return { name: 'card', cardId: second };
    case 'lessons':
      return second === undefined ? { name: 'lessons' } : notFound;
    case 'lesson':
      if (second === undefined || rest.length > 0 || !ID_PATTERN.test(second)) return notFound;
      return { name: 'lesson', lessonId: second };
    case 'problem':
      if (second === undefined || rest.length > 0 || !PROBLEM_ID_PATTERN.test(second)) {
        return notFound;
      }
      return { name: 'problem', problemId: second };
    case 'lab': {
      if (rest.length > 0) return notFound;
      const step = parseStep(query.get('step'));
      if (second === undefined) return { name: 'lab', step };
      return isLabKind(second) ? { name: 'lab', kind: second, step } : notFound;
    }
    case 'meter': {
      if (rest.length > 0) return notFound;
      if (second !== undefined && !ID_PATTERN.test(second)) return notFound;
      const route: Route = { name: 'meter' };
      if (second !== undefined) route.cardId = second;
      const n = parsePositiveInt(query.get('n'));
      if (n !== undefined) route.n = n;
      const depth = parseDepth(query.get('depth'));
      if (depth !== undefined) route.depth = depth;
      return route;
    }
    case 'settings':
      if (second === undefined) return { name: 'settings' };
      return second === 'data' && rest.length === 0
        ? { name: 'settings', section: 'data' }
        : notFound;
    case 'keys':
      return second === undefined ? { name: 'keys' } : notFound;
    default:
      return notFound;
  }
}

/** Route を `#/...` の文字列にする。parseHash(formatRoute(r)) は r と等しい（not-found を除く） */
export function formatRoute(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'session':
      return route.index === undefined ? '#/session' : `#/session/${route.index}`;
    case 'session-done':
      return '#/session/done';
    case 'card':
      return `#/card/${encodeURIComponent(route.cardId)}`;
    case 'lessons':
      return '#/lessons';
    case 'lesson': {
      const base = `#/lesson/${encodeURIComponent(route.lessonId)}`;
      return route.section === undefined || route.section === ''
        ? base
        : `${base}#${encodeURIComponent(route.section)}`;
    }
    case 'problem':
      return `#/problem/${encodeURIComponent(route.problemId)}`;
    case 'lab': {
      const base = route.kind === undefined ? '#/lab' : `#/lab/${route.kind}`;
      return route.step === 1 ? base : `${base}?step=${route.step}`;
    }
    case 'meter': {
      const base = route.cardId === undefined ? '#/meter' : `#/meter/${route.cardId}`;
      const params = new URLSearchParams();
      if (route.n !== undefined) params.set('n', String(route.n));
      if (route.depth !== undefined) params.set('depth', String(route.depth));
      const qs = params.toString();
      return qs === '' ? base : `${base}?${qs}`;
    }
    case 'settings':
      return route.section === 'data' ? '#/settings/data' : '#/settings';
    case 'keys':
      return '#/keys';
    case 'not-found':
      return '#/';
  }
}

/**
 * 主ナビの「現在地」。aria-current="page" を付ける項目を決める。
 * ドリル（session / card）はナビに項目が無いので null。
 */
export type NavSection = 'problems' | 'lessons' | 'lab' | 'meter' | 'settings' | null;

export function navSection(route: Route): NavSection {
  switch (route.name) {
    case 'home':
    case 'problem':
      return 'problems';
    case 'lessons':
    case 'lesson':
      return 'lessons';
    case 'session':
    case 'session-done':
    case 'card':
      return null;
    case 'lab':
      return 'lab';
    case 'meter':
      return 'meter';
    case 'settings':
    case 'keys':
      return 'settings';
    case 'not-found':
      return null;
  }
}

const LAB_TITLES: Record<LabKind, string> = {
  stack: 'スタック（LIFO）',
  queue: 'キュー（FIFO）',
  dict: '辞書と Set',
};
const STEP_TITLES: Record<LabStep, string> = { 1: '操作する', 2: '予想する', 3: 'コードで作る' };

export function labKindTitle(kind: LabKind): string {
  return LAB_TITLES[kind];
}

export function labStepTitle(step: LabStep): string {
  return STEP_TITLES[step];
}

/** 画面名（document.title の前半）。ドリルはカード種別と枚数が要るので画面側で上書きする */
export function routeTitle(route: Route): string {
  switch (route.name) {
    case 'home':
    case 'problem':
      return '問題';
    case 'session':
      return 'ドリル';
    case 'session-done':
      return 'セッション結果';
    case 'card':
      return 'カード';
    case 'lessons':
      return 'レッスン一覧';
    case 'lesson':
      return 'レッスン';
    case 'lab':
      return route.kind === undefined
        ? 'ラボ'
        : `${labKindTitle(route.kind)} ${labStepTitle(route.step)}`;
    case 'meter':
      return '計算量メーター';
    case 'settings':
      return route.section === 'data' ? 'データ' : '設定';
    case 'keys':
      return 'ショートカット';
    case 'not-found':
      return '問題';
  }
}

export const SITE_NAME = 'inductor-class';

export function documentTitle(screen: string): string {
  return `${screen} - ${SITE_NAME}`;
}
